import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    useGuestCards,
    UpdateGuestCardRequest,
    QuestionInput,
    ExistingQuestionAttachment,
} from '../../../../hooks/Outreach/useGuestCards'
import { useCustomQuestions } from '../../../../hooks/Outreach/useCustomQuestions'
import {
    GuestCard,
    GuestCardCustomQuestion,
    GuestCardRequiredFields,
} from '../../../../models/Outreach/types'
import {
    GUEST_CARD_DEFAULT_INCLUDE_FLAGS,
    GuestCardQuestionFieldKey,
    isGuestCardFieldLocked,
    requiredFieldsFromCard,
} from '../../../../models/Outreach/services'
import { GuestCardEditorCanvas } from './GuestCardEditorCanvas'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            maxWidth: '900px',
            width: '100%',
            height: '85vh',
            maxHeight: '85vh',
        },
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1.5),
    },
    titleText: {
        fontWeight: 'bold',
        color: '#1f2937',
        margin: 0,
        flexShrink: 0,
    },
    cardNameField: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#f9fafb',
        },
    },
    dialogContent: {
        padding: theme.spacing(3),
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        padding: theme.spacing(2, 3),
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    nameHint: {
        color: '#dc2626',
        fontSize: '0.8125rem',
        marginTop: theme.spacing(1),
        textAlign: 'right',
    },
    saveButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1.25, 3),
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)',
        },
        '&:disabled': {
            background: '#9ca3af',
            boxShadow: 'none',
        },
    },
    cancelButton: {
        textTransform: 'none',
        fontWeight: 500,
        padding: theme.spacing(1.25, 3),
        borderRadius: '8px',
        borderColor: '#d1d5db',
        color: '#6b7280',
    },
}))

interface EditGuestCardModalProps {
    open: boolean
    onClose: () => void
    guestCard: GuestCard | null
    onGuestCardUpdated: () => void
}

export const EditGuestCardModal: React.FC<EditGuestCardModalProps> = ({
    open,
    onClose,
    guestCard,
    onGuestCardUpdated,
}) => {
    const classes = useStyles()
    const { updateGuestCard } = useGuestCards()
    const { customQuestions } = useCustomQuestions()
    const [cardName, setCardName] = useState('')
    const [header, setHeader] = useState('')
    const [body, setBody] = useState('')
    const [includeFlags, setIncludeFlags] = useState({
        ...GUEST_CARD_DEFAULT_INCLUDE_FLAGS,
    })
    const [
        requiredFields,
        setRequiredFields,
    ] = useState<GuestCardRequiredFields>({})
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([])
    const [requiredQuestionIds, setRequiredQuestionIds] = useState<Set<number>>(
        new Set(),
    )
    const [extraQuestions, setExtraQuestions] = useState<
        GuestCardCustomQuestion[]
    >([])
    const [isUpdating, setIsUpdating] = useState(false)

    useEffect(() => {
        if (guestCard && open) {
            setCardName(guestCard.card_name || '')
            setHeader(guestCard.header || '')
            setBody(guestCard.body || '')
            setIncludeFlags({
                name: true,
                email: true,
                phone: guestCard.phone,
                preferred_unit_config: guestCard.preferred_unit_config,
                preferred_premiums: guestCard.preferred_premiums,
                preferred_rent_cost: guestCard.preferred_rent_cost,
                preferred_move_in_date: guestCard.preferred_move_in_date,
                preferred_move_out_date: guestCard.preferred_move_out_date,
                preferred_roommates: guestCard.preferred_roommates,
                school_attendance: guestCard.school_attendance,
                additional_requests: guestCard.additional_requests,
            })
            setRequiredFields(requiredFieldsFromCard(guestCard))
            const selected: number[] = []
            const required = new Set<number>()
            const attached: GuestCardCustomQuestion[] = []
            if (guestCard.card_questions) {
                const ordered = [...guestCard.card_questions].sort(
                    (a, b) => a.order - b.order,
                )
                ordered.forEach((cq) => {
                    selected.push(cq.custom_question.id)
                    attached.push(cq.custom_question)
                    if (cq.required) required.add(cq.custom_question.id)
                })
            }
            setSelectedQuestionIds(selected)
            setRequiredQuestionIds(required)
            setExtraQuestions(attached)
        }
    }, [guestCard, open])

    const handleRemoveField = (field: GuestCardQuestionFieldKey) => {
        if (isGuestCardFieldLocked(field)) return
        setIncludeFlags((prev) => ({ ...prev, [field]: false }))
    }

    const handleRestoreField = (field: GuestCardQuestionFieldKey) => {
        setIncludeFlags((prev) => ({ ...prev, [field]: true }))
    }

    const handleToggleRequired = (
        field: GuestCardQuestionFieldKey,
        required: boolean,
    ) => {
        if (isGuestCardFieldLocked(field)) return
        setRequiredFields((prev) => ({ ...prev, [field]: required }))
    }

    const handleAddCustomQuestion = (
        question: GuestCardCustomQuestion,
        required = true,
    ) => {
        setSelectedQuestionIds((prev) =>
            prev.includes(question.id) ? prev : prev.concat(question.id),
        )
        setExtraQuestions((prev) =>
            prev.some((item) => item.id === question.id)
                ? prev
                : prev.concat(question),
        )
        if (required) {
            setRequiredQuestionIds((prev) => new Set(prev).add(question.id))
        }
    }

    const handleRemoveCustomQuestion = (questionId: number) => {
        setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId))
        setRequiredQuestionIds((prev) => {
            const next = new Set(prev)
            next.delete(questionId)
            return next
        })
    }

    const handleToggleCustomRequired = (
        questionId: number,
        required: boolean,
    ) => {
        setRequiredQuestionIds((prev) => {
            const next = new Set(prev)
            if (required) next.add(questionId)
            else next.delete(questionId)
            return next
        })
    }

    const handleSave = async () => {
        if (!guestCard || !cardName.trim()) {
            toast.error('Card name is required')
            return
        }

        setIsUpdating(true)
        const questions: QuestionInput[] = selectedQuestionIds.map(
            (id, idx) =>
                ({
                    id,
                    order: idx,
                    required: requiredQuestionIds.has(id),
                } as ExistingQuestionAttachment),
        )

        const request: UpdateGuestCardRequest = {
            id: guestCard.id,
            card_name: cardName.trim(),
            header: header.trim(),
            body: body.trim(),
            name: true,
            email: true,
            phone: includeFlags.phone,
            preferred_unit_config: includeFlags.preferred_unit_config,
            preferred_premiums: includeFlags.preferred_premiums,
            preferred_rent_cost: includeFlags.preferred_rent_cost,
            preferred_move_in_date: includeFlags.preferred_move_in_date,
            preferred_move_out_date: includeFlags.preferred_move_out_date,
            preferred_roommates: includeFlags.preferred_roommates,
            school_attendance: includeFlags.school_attendance,
            additional_requests: includeFlags.additional_requests,
            required_fields: requiredFields,
            questions,
        }

        try {
            await updateGuestCard.mutateAsync(request)
            onGuestCardUpdated()
        } finally {
            setIsUpdating(false)
        }
    }

    if (!guestCard) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Box className={classes.titleRow}>
                    <Typography variant="h6" className={classes.titleText}>
                        Edit Guest Card
                    </Typography>
                    <TextField
                        className={classes.cardNameField}
                        size="small"
                        variant="outlined"
                        label="Guest card name"
                        placeholder="e.g., Standard Guest Card"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <GuestCardEditorCanvas
                    header={header}
                    body={body}
                    onHeaderChange={setHeader}
                    onBodyChange={setBody}
                    includeFlags={includeFlags}
                    onRemoveField={handleRemoveField}
                    onRestoreField={handleRestoreField}
                    requiredFields={requiredFields}
                    onToggleRequired={handleToggleRequired}
                    customQuestions={[
                        ...(customQuestions || []),
                        ...extraQuestions.filter(
                            (question) =>
                                !(customQuestions || []).some(
                                    (item) => item.id === question.id,
                                ),
                        ),
                    ]}
                    selectedQuestionIds={selectedQuestionIds}
                    requiredQuestionIds={requiredQuestionIds}
                    onAddCustomQuestion={handleAddCustomQuestion}
                    onRemoveCustomQuestion={handleRemoveCustomQuestion}
                    onToggleCustomRequired={handleToggleCustomRequired}
                />
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Box className={classes.actionButtons}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        className={classes.saveButton}
                        disabled={isUpdating || !cardName.trim()}
                    >
                        {isUpdating ? 'Updating...' : 'Update Guest Card'}
                    </Button>
                </Box>
                {!cardName.trim() && (
                    <Typography className={classes.nameHint}>
                        You must set a name for the guest card before you can
                        update it.
                    </Typography>
                )}
            </DialogActions>
        </Dialog>
    )
}
